import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiRefreshCw, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService } from '../api/services/dashboardService';
import { GameSessionDetail, PaginatedResponse, TableCountsResponse, GameTestRankItem } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

interface GameSession {
  id: number;
  gameId: string;
  maxLevel: number;
  exitReason: string;
  startedAt: string;
  endedAt: string;
  startedAtOriginal: string | null;
  endedAtOriginal: string | null;
  durationSeconds: number;
}

type SortColumn = 'id' | 'gameId' | 'maxLevel' | 'startedAt' | 'endedAt' | 'durationSeconds';
type SortDirection = 'asc' | 'desc';

const GameSessions: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 25;
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [allGameSessionsForChart, setAllGameSessionsForChart] = useState<GameSessionDetail[]>([]);
  const [gameRankData, setGameRankData] = useState<GameTestRankItem[]>([]);
  const [chartLoading, setChartLoading] = useState<boolean>(true);
  const [hiddenGames, setHiddenGames] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [tableCounts, setTableCounts] = useState<TableCountsResponse | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Fetch game sessions from API
  const fetchGameSessions = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response: PaginatedResponse<GameSessionDetail> = await dashboardService.getGameSessionDetails({
        page,
      });

      // Transform API data to local format
      const transformedSessions: GameSession[] = response.data.map((sessionDetail) => ({
        id: sessionDetail.game_session_id,
        gameId: sessionDetail.game_id,
        maxLevel: sessionDetail.max_level,
        exitReason: sessionDetail.exit_reason || '-',
        startedAt: formatDate(sessionDetail.started_at),
        endedAt: formatDate(sessionDetail.ended_at),
        startedAtOriginal: sessionDetail.started_at,
        endedAtOriginal: sessionDetail.ended_at,
        durationSeconds: sessionDetail.duration_seconds,
      }));

      setSessions(transformedSessions);
      setPagination({
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching game sessions:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch table counts for stats
  const fetchTableCounts = async () => {
    try {
      const response = await dashboardService.getTableCounts();
      setTableCounts(response);
    } catch (err: any) {
      console.error('Error fetching table counts:', err);
    }
  };

  // Fetch game sessions for chart (multiple pages) - DEPRECATED, using new API
  const fetchGameSessionsForChart = async () => {
    try {
      const allSessions: GameSessionDetail[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 10) { // Limit to 10 pages to avoid too many requests
        try {
          const response = await dashboardService.getGameSessionDetails({ page });
          allSessions.push(...response.data);
          hasMore = response.data.length > 0 && response.pagination.page < response.pagination.totalPages;
          page++;
        } catch (err) {
          console.error('Error fetching game sessions page for chart:', err);
          hasMore = false;
        }
      }
      
      setAllGameSessionsForChart(allSessions);
    } catch (err: any) {
      console.error('Error fetching game sessions for chart:', err);
    }
  };

  // Fetch game rank data for chart from new API
  const fetchGameRankData = async () => {
    try {
      setChartLoading(true);
      const response = await dashboardService.getGameTestRank({ type: 'games' });
      setGameRankData(response);
    } catch (err: any) {
      console.error('Error fetching game rank data:', err);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchGameSessions(currentPage);
    fetchTableCounts();
    fetchGameRankData();
  }, [currentPage]);

  // Track if we've initialized hidden games to prevent infinite loops
  const initializedGamesRef = useRef(false);

  // Initialize hidden games: hide all except first 3
  useEffect(() => {
    if (gameRankData.length > 0 && !initializedGamesRef.current) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const allGameIds = Array.from(new Set(gameRankData
        .filter(item => {
          if (!item.started_at) return false;
          const itemDate = new Date(item.started_at);
          return itemDate <= today;
        })
        .map(item => item.id)
      )).sort();
      
      const gamesToHide = allGameIds.slice(3); // Hide from 4th game onwards
      if (gamesToHide.length > 0) {
        setHiddenGames(new Set(gamesToHide));
        initializedGamesRef.current = true;
      }
    }
  }, [gameRankData]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchGameSessions(currentPage);
    fetchTableCounts();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
    setCurrentPage(page);
    }
  };
  // Get daily change from metrics (daily growth percentage)
  const getDailyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get weekly change from metrics (weekly growth percentage)
  const getWeeklyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_weekly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get monthly change from metrics (monthly growth percentage)
  const getMonthlyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_monthly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get today's count by metric type
  const getTodayCount = (metricType: string): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
    return count?.total_today || 0;
  };

  // Get today's average duration by metric type
  const getAvgDurationToday = (metricType: string): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
    return count?.avg_duration_today || 0;
  };

  // Get daily change from Daily metrics (daily growth percentage)
  const getDailyValue = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  const stats = [
    { 
      label: 'Toplam Oynanan Oyun', 
      value: pagination.total.toLocaleString('tr-TR'), 
      change: getDailyChange('game_session'), 
      weeklyChange: getWeeklyChange('game_session'),
      monthlyChange: getMonthlyChange('game_session'),
      color: 'blue' 
    },
    { 
      label: 'Oynanan Oyun', 
      value: getTodayCount('game_session').toLocaleString('tr-TR'), 
      color: 'green' 
    },
    { 
      label: 'Ortalama Süre', 
      value: formatDuration(getAvgDurationToday('game_session')), 
      color: 'purple' 
    },
  ];

  // Process game rank data for daily chart from new API
  const processGameSessionsChartData = useMemo(() => {
    if (gameRankData.length === 0) return [];

    // Filter out future dates (only show dates up to today)
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    const filteredData = gameRankData.filter(item => {
      if (!item.started_at) return false;
      const itemDate = new Date(item.started_at);
      return itemDate <= today;
    });

    if (filteredData.length === 0) return [];

    // Group by date and game id
    const dateGameGroups = filteredData.reduce((acc, item) => {
      if (!item.started_at) return acc;

      const date = item.started_at;
      const gameId = item.id;

      if (!acc[date]) {
        acc[date] = {};
      }

      if (!acc[date][gameId]) {
        acc[date][gameId] = 0;
      }

      acc[date][gameId] += item.count;

      return acc;
    }, {} as Record<string, Record<string, number>>);

    // Get all unique game IDs
    const allGameIds = Array.from(new Set(filteredData.map(item => item.id))).sort();

    // Get all unique dates and sort
    const dates = Object.keys(dateGameGroups).sort();

    // Build chart data - show all days
    return dates.map(date => {
      const dataPoint: any = {
        name: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
        date: date,
      };

      // Add count for each game
      allGameIds.forEach(gameId => {
        dataPoint[gameId] = dateGameGroups[date]?.[gameId] || 0;
      });

      return dataPoint;
    });
  }, [gameRankData]);

  // Handle sort
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort sessions
  const sortedSessions = useMemo(() => {
    if (!sortColumn) return sessions;

    return [...sessions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'gameId':
          aValue = a.gameId.toLowerCase();
          bValue = b.gameId.toLowerCase();
          break;
        case 'maxLevel':
          aValue = a.maxLevel;
          bValue = b.maxLevel;
          break;
        case 'startedAt':
          aValue = a.startedAtOriginal ? new Date(a.startedAtOriginal).getTime() : 0;
          bValue = b.startedAtOriginal ? new Date(b.startedAtOriginal).getTime() : 0;
          break;
        case 'endedAt':
          aValue = a.endedAtOriginal ? new Date(a.endedAtOriginal).getTime() : 0;
          bValue = b.endedAtOriginal ? new Date(b.endedAtOriginal).getTime() : 0;
          break;
        case 'durationSeconds':
          aValue = a.durationSeconds;
          bValue = b.durationSeconds;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sessions, sortColumn, sortDirection]);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Oyun Oturumları
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`relative glass-strong rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 border overflow-hidden group transform hover:-translate-y-1 animate-fade-in ${
            stat.color === 'blue' ? 'hover:border-blue-300 dark:hover:border-blue-600' :
            stat.color === 'green' ? 'hover:border-green-300 dark:hover:border-green-600' :
            stat.color === 'purple' ? 'hover:border-purple-300 dark:hover:border-purple-600' :
            'hover:border-indigo-300 dark:hover:border-indigo-600'
          }`} style={{ animationDelay: `${index * 100}ms` }}>
            {/* Animated gradient background */}
            <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 group-hover:scale-150 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
              'bg-indigo-100 dark:bg-indigo-900/30'
            }`}></div>
            <div className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
              'bg-indigo-100 dark:bg-indigo-900/30'
            }`}></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 shimmer"></div>
            </div>
            
            <div className="relative z-10">
              <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">{stat.label}</p>
              <p className={`text-5xl font-extrabold mb-4 bg-gradient-to-r ${
                stat.color === 'blue' ? 'from-blue-600 via-blue-500 to-blue-400' :
                stat.color === 'green' ? 'from-green-600 via-green-500 to-green-400' :
                stat.color === 'purple' ? 'from-purple-600 via-purple-500 to-purple-400' :
                'from-indigo-600 via-indigo-500 to-indigo-400'
              } bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
                {stat.value}
              </p>
              {stat.label === 'Toplam Oynanan Oyun' && (
                <div className="flex flex-col gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  {stat.change && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Günlük</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.change.startsWith('+') || stat.change.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.change}
                      </span>
                    </div>
                  )}
                  {stat.weeklyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Haftalık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.weeklyChange.startsWith('+') || stat.weeklyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.weeklyChange}
                      </span>
                    </div>
                  )}
                  {stat.monthlyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aylık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.monthlyChange.startsWith('+') || stat.monthlyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.monthlyChange}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Game Sessions Daily Duration Chart */}
      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-6 border transform hover:scale-[1.01] animate-fade-in">
        <h2 className="text-2xl font-bold gradient-text mb-6">Oyun Oturumları - Günlük Oynanma Sayısı ve Grafik</h2>
        {chartLoading ? (
          <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
            Yükleniyor...
          </div>
        ) : processGameSessionsChartData.length === 0 ? (
          <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
            Veri bulunamadı
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={processGameSessionsChartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'} />
              <XAxis 
                dataKey="name" 
                stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis 
                domain={[0, 'dataMax + 10']} 
                stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                label={{ value: 'Oturum Sayısı', angle: -90, position: 'insideLeft' }}
              />
            <Legend 
              wrapperStyle={{
                color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
                cursor: 'pointer'
              }}
              onClick={(e: any) => {
                const gameId = e.dataKey;
                if (gameId) {
                  setHiddenGames(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(gameId)) {
                      newSet.delete(gameId);
                    } else {
                      newSet.add(gameId);
                    }
                    return newSet;
                  });
                }
              }}
              iconType="line"
            />
            {(() => {
              // Get all unique game IDs from filtered data
              const today = new Date();
              today.setHours(23, 59, 59, 999);
              const allGameIds = Array.from(new Set(gameRankData
                .filter(item => {
                  if (!item.started_at) return false;
                  const itemDate = new Date(item.started_at);
                  return itemDate <= today;
                })
                .map(item => item.id)
              )).sort();

              return allGameIds.map((gameId, index) => {
                const hue = (index * 137) % 360; // Use a prime number for better color distribution
                const color = `hsl(${hue}, 70%, 50%)`;
                const isHidden = hiddenGames.has(gameId);
                
                return (
                  <Line 
                    key={gameId}
                    type="monotone" 
                    dataKey={gameId} 
                    stroke={color} 
                    strokeWidth={2} 
                    dot={{ r: 3 }} 
                    name={gameId}
                    hide={isHidden}
                    strokeOpacity={isHidden ? 0 : 1}
                  />
                );
              });
            })()}
          </LineChart>
        </ResponsiveContainer>
        )}
      </div>

      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border transform hover:scale-[1.01] animate-fade-in">
        <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-indigo-50/50 via-purple-50/50 to-pink-50/50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm flex items-center justify-between">
          <h2 className="text-xl font-bold gradient-text">Oyun Oturumları</h2>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-800 dark:text-white bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 transform disabled:hover:scale-100"
          >
            {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
            <span>Yenile</span>
          </button>
        </div>
        {error && (
          <div className="px-6 py-4 glass bg-gradient-to-r from-red-50/80 to-rose-50/80 dark:from-red-900/30 dark:to-rose-900/30 border-b border-red-200/50 dark:border-red-800/50">
            <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
              <span className="text-xl">⚠️</span>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-2">
                    <span>ID</span>
                    {sortColumn === 'id' && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4")}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort('gameId')}
                >
                  <div className="flex items-center gap-2">
                    <span>Game ID</span>
                    {sortColumn === 'gameId' && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4")}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort('maxLevel')}
                >
                  <div className="flex items-center gap-2">
                    <span>Max Level</span>
                    {sortColumn === 'maxLevel' && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4")}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort('startedAt')}
                >
                  <div className="flex items-center gap-2">
                    <span>Başlangıç</span>
                    {sortColumn === 'startedAt' && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4")}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort('endedAt')}
                >
                  <div className="flex items-center gap-2">
                    <span>Bitiş</span>
                    {sortColumn === 'endedAt' && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4")}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort('durationSeconds')}
                >
                  <div className="flex items-center gap-2">
                    <span>Süre (s)</span>
                    {sortColumn === 'durationSeconds' && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4")}
                      </span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {sortedSessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Oyun oturumu bulunamadı
                  </td>
                </tr>
              ) : (
                sortedSessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">{session.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.gameId}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.maxLevel}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.startedAt}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.endedAt}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900 dark:text-white">{session.durationSeconds}s</td>
                </tr>
                ))
              )}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan {sessions.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-{Math.min(currentPage * itemsPerPage, pagination.total)} arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Önceki
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 10) {
                    pageNum = i + 1;
                  } else if (currentPage <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 4) {
                    pageNum = pagination.totalPages - 9 + i;
                  } else {
                    pageNum = currentPage - 5 + i;
                  }
                  return (
                  <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 ${
                        currentPage === pageNum
                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 text-white shadow-lg scale-110'
                        : 'text-slate-700 dark:text-slate-300 glass border border-gray-300/50 dark:border-gray-600/50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 hover:scale-105 transform shadow-sm hover:shadow-md'
                    }`}
                  >
                      {pageNum}
                  </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameSessions;

